import BigNumber from 'bignumber.js';

// Shared with Java Replicator. Amounts and returns stay decimal strings until
// presentation; a return of "0.03" means 3%, not 0.03%.
export type PerformanceSubject = 'PORTFOLIO' | 'FUND_HOLDING';
export type PerformanceStatus = 'READY' | 'PROVISIONAL' | 'UNAVAILABLE';
export interface InvestmentPerformance {
  id: string;
  schemaVersion: 2;
  year: number;
  subjectType: PerformanceSubject;
  subjectId: string;
  customerId: string;
  productId: string;
  currency: 'KWD';
  periodStart: string;
  firstInvestedDate?: string | null;
  periodEnd: string;
  asOfDate: string;
  openingValue: string | null;
  closingValue: string | null;
  contributions: string | null;
  withdrawals: string | null;
  // Fund-holding documents only. Portfolio documents omit it: Geneva books
  // every cash paid out as a withdrawal and retained dividends are already in
  // the value and return, so a separate figure has no meaning there.
  distributions?: string | null;
  gainLoss: string | null;
  ytdReturn: string | null;
  status: PerformanceStatus;
  // Quality reasons: any entry means PROVISIONAL or UNAVAILABLE and needs attention.
  reasons: string[];
  // Informational only (e.g. the rolling-refresh sentence); never changes status.
  notes: string[];
  method: 'DAILY_LINKED_EOD_V1';
  points: InvestmentPerformancePoint[];
  calculatedAt: string;
  runId: string;
}

export interface InvestmentPerformancePoint {
  date: string;
  value: string | null;
  netFlow: string | null;
  gainLoss: string | null;
  ytdReturn: string | null;
}

const amountFields = ['openingValue', 'closingValue', 'contributions', 'withdrawals', 'gainLoss', 'ytdReturn'] as const;
const isStringArray = (value: unknown): value is string[] => Array.isArray(value) && value.every(entry => typeof entry === 'string');
const pointFields = ['value', 'netFlow', 'gainLoss', 'ytdReturn'] as const;
const decimalPattern = /^-?\d+(?:\.\d+)?$/;
const isDecimal = (value: unknown): value is string | null => value === null || (typeof value === 'string' && decimalPattern.test(value));
export const isIsoDate = (value: unknown): value is string => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
};
export const isPerformancePathPart = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && !value.includes('/') && value !== '.' && value !== '..';

export function parseInvestmentPerformance(id: string, raw: Record<string, unknown>, year: number): InvestmentPerformance {
  if (!isPerformancePathPart(id) || raw.schemaVersion !== 2 || raw.year !== year || !['PORTFOLIO', 'FUND_HOLDING'].includes(String(raw.subjectType)) ||
      !isPerformancePathPart(raw.subjectId) || !isPerformancePathPart(raw.customerId) || !isPerformancePathPart(raw.productId) || raw.currency !== 'KWD' ||
      !isIsoDate(raw.periodStart) || !isIsoDate(raw.periodEnd) || !isIsoDate(raw.asOfDate) ||
      (raw.firstInvestedDate !== undefined && raw.firstInvestedDate !== null && !isIsoDate(raw.firstInvestedDate)) ||
      raw.periodStart > raw.periodEnd || raw.asOfDate !== raw.periodEnd || Number(raw.periodEnd.slice(0, 4)) !== year ||
      !amountFields.every(field => isDecimal(raw[field])) ||
      (raw.distributions !== undefined && !isDecimal(raw.distributions)) ||
      !['READY', 'PROVISIONAL', 'UNAVAILABLE'].includes(String(raw.status)) ||
      !isStringArray(raw.reasons) ||
      (raw.notes !== undefined && !isStringArray(raw.notes)) ||
      raw.method !== 'DAILY_LINKED_EOD_V1' ||
      'activeRevision' in raw ||
      typeof raw.calculatedAt !== 'string' || !Number.isFinite(Date.parse(raw.calculatedAt)) || !isPerformancePathPart(raw.runId) ||
      (raw.status === 'READY' && (raw.ytdReturn === null || raw.gainLoss === null))) {
    throw new Error('The published performance record is incomplete or uses an unsupported format.');
  }
  // Older documents predate `notes`; an absent `distributions` stays absent so
  // the presentation can tell "not applicable" apart from "unavailable" (null).
  const summary = { ...raw, id, notes: raw.notes ?? [] } as unknown as InvestmentPerformance;
  return { ...summary, points: parsePerformancePoints(raw.points, summary) };
}

export function parsePerformancePoints(points: unknown, summary: Pick<InvestmentPerformance, 'year' | 'periodStart' | 'periodEnd' | 'closingValue' | 'gainLoss' | 'ytdReturn'>): InvestmentPerformancePoint[] {
  if (!Array.isArray(points)) throw new Error('The daily history is incomplete.');
  const seen = new Set<string>();
  const result: InvestmentPerformancePoint[] = [];
  for (const raw of points) {
    if (!raw || typeof raw !== 'object' || !isIsoDate(raw.date) ||
        raw.date > summary.periodEnd || raw.date < `${summary.year - 1}-12-31` ||
        !pointFields.every(field => isDecimal(raw[field])) || seen.has(raw.date)) {
      throw new Error('The daily history has invalid or duplicate dates.');
    }
    seen.add(raw.date);
    result.push(raw as InvestmentPerformancePoint);
  }
  result.sort((a, b) => a.date.localeCompare(b.date));
  if (!result.length || result[0].date > summary.periodStart || result[result.length - 1].date !== summary.periodEnd) throw new Error('The daily history has not been published completely.');
  const last = result[result.length - 1];
  const equalDecimal = (left: string | null, right: string | null) => left === null || right === null ? left === right : new BigNumber(left).isEqualTo(right);
  if (!equalDecimal(last.value, summary.closingValue) || !equalDecimal(last.ytdReturn, summary.ytdReturn) || !equalDecimal(last.gainLoss, summary.gainLoss)) {
    throw new Error('The daily history does not match the published summary.');
  }
  // Fill omitted dates with nulls so a chart cannot draw across missing evidence.
  const pointsByDate = new Map(result.map(point => [point.date, point]));
  const completed: InvestmentPerformancePoint[] = [];
  for (let date = result[0].date; date <= summary.periodEnd; date = new Date(Date.parse(`${date}T00:00:00Z`) + 86400000).toISOString().slice(0, 10)) {
    completed.push(pointsByDate.get(date) ?? { date, value: null, netFlow: null, gainLoss: null, ytdReturn: null });
  }
  return completed;
}

export function kuwaitDate(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuwait', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

export const kuwaitYear = (now = new Date()) => Number(kuwaitDate(now).slice(0, 4));

// A daily run can legitimately be dated yesterday. Older results remain usable
// but must be visibly dated; freshness never changes the calculation status.
export function isPerformanceStale(summary: InvestmentPerformance, now = new Date()): boolean {
  const age = Date.parse(`${kuwaitDate(now)}T00:00:00Z`) - Date.parse(`${summary.asOfDate}T00:00:00Z`);
  const publicationAge = now.getTime() - Date.parse(summary.calculatedAt);
  return age > 2 * 86400000 || publicationAge > 2 * 86400000;
}

export function formatPerformanceAmount(value: string | null): string {
  return value === null ? '—' : `${new BigNumber(value).toFormat(3)} KWD`;
}

export function formatPerformanceReturn(value: string | null): string {
  return value === null ? '—' : `${new BigNumber(value).multipliedBy(100).toFormat(2)}%`;
}

const reasonLabels: Record<string, string> = {
  MISSING_UNIT_LEDGER: 'The complete history of fund units is missing.',
  UNKNOWN_UNIT_TRANSACTION_TYPE: 'A fund transaction type needs classification.',
  INVALID_DIRECTIONAL_UNITS: 'A transaction has an unexpected unit direction.',
  UNPROVED_OPENING_UNIT_BALANCE: 'The opening fund units have not been reconciled.',
  MISSING_PRIOR_NAV: 'A historical fund NAV is missing.',
  CONFLICTING_OR_INVALID_NAV: 'Historical fund NAV records conflict or are invalid.',
  STALE_NAV: 'The available fund NAV is too old for part of this period.',
  MISSING_FX: 'A historical exchange rate is missing.',
  CONFLICTING_FX: 'Historical exchange-rate records conflict.',
  STALE_FX: 'The available exchange rate is too old for part of this period.',
  UNSUPPORTED_FUND_CURRENCY: 'Historical valuation is not supported for this fund currency.',
  AMBIGUOUS_CUSTOMER_MAPPING: 'The holding’s customer ownership needs reconciliation.',
  CURRENT_UNITS_MISMATCH: 'Replayed fund units differ from the current recorded holding.',
  GENEVA_UNITS_MISMATCH: 'Replayed fund units differ from Geneva’s recorded balances.',
  GENEVA_UNIT_EVENT_MISMATCH: 'A unit movement differs from the Geneva transaction record.',
  NEGATIVE_FUND_UNITS: 'The transaction history produces a negative unit balance.',
  UNIT_ADJUSTMENT_HAS_CASH: 'A unit adjustment includes a cash amount that needs review.',
  UNIT_ADJUSTMENT_RETURN_EFFECT_REQUIRES_REVIEW: 'A unit adjustment may affect the return and needs review.',
  MISSING_OR_CONFLICTING_FLOW_CURRENCY: 'A transaction currency is missing or differs from the fund currency.',
  INTERNAL_FUND_TRANSFER_UNBALANCED: 'An internal transfer does not balance.',
  UNRESOLVED_DISTRIBUTION_REINVESTMENT: 'A reinvested distribution needs reconciliation.',
  ZERO_CASH_SUBSCRIPTION_REQUIRES_REVIEW: 'A subscription adds units without a recorded cash amount.',
  REDEMPTION_FEES_EXCEED_AMOUNT: 'Recorded redemption fees exceed the payment amount.',
  FUND_FEE_GROSS_NET_BASIS_REQUIRES_REVIEW: 'The treatment of recorded fees needs confirmation.',
  FUND_FEE_COVERAGE_UNCONFIRMED: 'Complete fee coverage has not been confirmed.',
  ESTIMATED_FUND_FLOW: 'A transaction amount is still estimated.',
  UNRECONCILED_FUND_DISTRIBUTION: 'An announced fund distribution has not been reconciled to this holding.',
  INVALID_FUND_DISTRIBUTION_CONTROL: 'A distribution record needs reconciliation.',
};

export function performanceReasonText(reason: string): string {
  return reason.split(';').map(part => {
    const value = part.trim();
    if (reasonLabels[value]) return reasonLabels[value];
    // Geneva provides a diagnostic code followed by an explanatory sentence.
    const explanatory = value.replace(/^[A-Z][A-Z0-9_]+:\s*/, '');
    if (explanatory !== value) return explanatory.charAt(0).toUpperCase() + explanatory.slice(1);
    if (/^[A-Z][A-Z0-9_]+$/.test(value)) {
      const words = value.toLowerCase().replace(/_/g, ' ');
      return words.charAt(0).toUpperCase() + words.slice(1) + '.';
    }
    return value;
  }).join(' ');
}

// Admin-only while the published figures are validated; mirrors firestore.rules.
export function canReadInvestmentPerformance(customer: { employee?: boolean }, claims?: { admin?: boolean; kfh_rm?: boolean; routeGroups?: string[] }): boolean {
  return customer.employee !== true && claims?.admin === true && claims?.kfh_rm !== true && !!claims?.routeGroups?.some(group => ['customers', 'orders', 'service', 'portfolios', 'transactions', 'global_dpm'].includes(group));
}

export interface PerformanceTotalPoint { date: string; value: number }

// Daily total of several investments' published values. A date before an
// investment's first point counts as 0 (not yet held); a date after its last
// point, or a gap inside its series, is unknown and is left out of the total
// so lagging publications never show up as a drop.
export function totalPerformanceValues(summaries: InvestmentPerformance[]): PerformanceTotalPoint[] {
  const series = summaries
    .filter(summary => summary.status !== 'UNAVAILABLE' && summary.points.length > 0)
    .map(summary => ({ first: summary.points[0].date, last: summary.points[summary.points.length - 1].date, byDate: new Map(summary.points.map(point => [point.date, point.value])) }));
  if (series.length === 0) return [];
  const dates = [...new Set(series.flatMap(entry => [...entry.byDate.keys()]))].sort();
  const totals: PerformanceTotalPoint[] = [];
  for (const date of dates) {
    let total = 0;
    let known = true;
    for (const entry of series) {
      if (date < entry.first) continue;
      const value = date > entry.last ? null : entry.byDate.get(date) ?? null;
      if (value === null) { known = false; break; }
      total += Number(value);
    }
    if (known) totals.push({ date, value: total });
  }
  return totals;
}

export interface InvestmentPerformanceTarget {
  subject: PerformanceSubject;
  investmentId: string;
}

export const performanceInvestmentKey = (subject: PerformanceSubject, investmentId: string) => `${subject}/${investmentId}`;

export function performanceDocumentPath(customerId: string, target: InvestmentPerformanceTarget): string[] {
  if (!isPerformancePathPart(customerId) || !isPerformancePathPart(target.investmentId) ||
      !['PORTFOLIO', 'FUND_HOLDING'].includes(target.subject)) throw new Error('The investment document path is invalid.');
  return ['customers', customerId, target.subject === 'PORTFOLIO' ? 'portfolios' : 'fund_data', target.investmentId, 'performance', 'current'];
}
